import clsx from 'clsx';
import { RefObject } from 'preact';
import { useEffect, useId, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import CameraSelector from '@/components/HabitatLiveStreamPlayer/Controls/CameraSelector';
import { zlSwitchCamera } from '@/components/icons';
import StreamControls from '@/components/LiveStream/StreamInteractiveArea/StreamControls';
import { useLiveStreamPlayerStateContext } from '@/components/LiveStreamPlayer/contexts';
import LiveStreamPlayerControlsFull from '@/components/LiveStreamPlayer/LiveStreamPlayerControls/Full';
import LockBadge from '@/components/LockBadge';
import { setMembersOnlyWarningModalState } from '@/components/MembersOnlyWarningModal/redux/actions';
import SwitchControl from '@/components/SwitchControl';
import { FullscreenControlButton, IconButton, SoundControlButtonGroup } from '@/components/VideoControls/buttons';
import { useSelector } from '@/redux/helper';
import { selectIsFreeUser } from '@/redux/selectors/users';

import BackgroundMusicSelector from '../BackgroundMusicSelector';

interface HomeLiveStreamPlayerDesktopControlsProps {
  containerRef: RefObject<HTMLDivElement>;
  miniMode?: boolean;
  isAmbientMode?: boolean;
  isMuted: boolean;
  volume: number;
  onAmbientModeChange?: (isAmbientMode: boolean) => void;
  onMutedChange?: (isMuted: boolean) => void;
  onVolumeChange: (volume: number) => void;
}

const HomeLiveStreamPlayerDesktopControls = ({
  containerRef,
  miniMode,
  isMuted,
  onMutedChange,
  isAmbientMode,
  onAmbientModeChange,
  volume,
  onVolumeChange,
}: HomeLiveStreamPlayerDesktopControlsProps) => {
  const id = useId();
  const isFreeUser = useSelector(selectIsFreeUser);
  const logged = useSelector((state) => state.user.logged);
  const dispatch = useDispatch();
  const [isMinimized, setIsMinimized] = useState(true);
  const { isVideoLoaded, requestFullscreenFromIFrame } = useLiveStreamPlayerStateContext();
  const hasAmbientModeBackgroundMusics = useSelector((state) => state.remoteConfig.ambientModeBackgroundMusics.length > 0);

  // auto minimize the controls when the user is not interacting with the container
  useEffect(() => {
    const div = containerRef.current;
    if (!div) {
      return undefined;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let lastTouchEndTime: number | null = null;

    const handleMouseEnter = () => {
      setIsMinimized(false);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const handleMouseLeave = () => {
      // Sometimes the mouse leave event is triggered after the touch end event,
      // so we ignore the mouse leave event if the touch end event happened within the last 300ms and the timeout is still active
      if (lastTouchEndTime && Date.now() - lastTouchEndTime < 300 && timeoutId) {
        return;
      }
      setIsMinimized(true);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const handleMouseMove = () => {
      setIsMinimized(false);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        setIsMinimized(true);
      }, 3000);
    };

    const handleTouchEnd = () => {
      lastTouchEndTime = Date.now();
      handleMouseMove(); // we want to treat touch end as a mouse move to keep the controls visible for a few seconds
    };

    div.addEventListener('touchend', handleTouchEnd);
    div.addEventListener('mouseenter', handleMouseEnter);
    div.addEventListener('mouseleave', handleMouseLeave);
    div.addEventListener('mousemove', handleMouseMove);

    return () => {
      div.removeEventListener('touchend', handleTouchEnd);
      div.removeEventListener('mouseenter', handleMouseEnter);
      div.removeEventListener('mouseleave', handleMouseLeave);
      div.removeEventListener('mousemove', handleMouseMove);

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [containerRef]);

  return (
    <div
      data-minimized={isMinimized}
      className={clsx('group/desktop-controls absolute inset-0 data-[minimized=true]:cursor-none', miniMode && '!hidden')}
    >
      {isAmbientMode && (
        <BackgroundMusicSelector
          volume={volume}
          muted={isMuted}
          isExpanded={!isMinimized}
          className="absolute bottom-2 left-4 transition-transform group-data-[minimized=false]/desktop-controls:-translate-y-8"
        />
      )}
      <LiveStreamPlayerControlsFull.Container
        className={clsx(
          'translate-y-full bg-transparent bg-gradient-to-t from-black/25 to-transparent px-2 transition-transform group-data-[minimized=false]/desktop-controls:translate-y-0',
        )}
      >
        <LiveStreamPlayerControlsFull.ButtonsGroup>
          <LiveStreamPlayerControlsFull.PlayPauseButton disabled={!isVideoLoaded} />
          <SoundControlButtonGroup
            orientation="vertical"
            disabled={!isVideoLoaded && !isAmbientMode}
            volume={volume}
            onVolumeChange={onVolumeChange}
            isMuted={isMuted}
            onMutedChange={onMutedChange}
            rangeInputContainerClassName="bg-grey-1 shadow-none"
          />
          <FullscreenControlButton fullscreenElementRef={containerRef} onFailToFullscreen={requestFullscreenFromIFrame} />
          <CameraSelector
            renderButton={({ onClick }) => <IconButton label="Switch camera" icon={zlSwitchCamera} onClick={onClick} />}
          />
        </LiveStreamPlayerControlsFull.ButtonsGroup>
      </LiveStreamPlayerControlsFull.Container>
      {isAmbientMode != null && hasAmbientModeBackgroundMusics && (
        <div className="absolute bottom-2 right-4">
          <div className="inline-flex flex-row items-center gap-2 rounded-full bg-white/60 px-2 py-1">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={`${id}-ambient-mode`} className="ml-1 text-xs font-bold">
              Ambiance Mode
            </label>
            <SwitchControl
              id={`${id}-ambient-mode`}
              className="aria-checked:bg-[#C89400]"
              size="small"
              checked={isAmbientMode}
              onChange={onAmbientModeChange}
            />
          </div>
          {isFreeUser && <LockBadge className="absolute -top-0.5 right-0.5" />}
        </div>
      )}
      {!logged && (
        <div
          onClickCapture={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dispatch(
              setMembersOnlyWarningModalState({
                type: 'ambient-mode',
                redirectOnDismiss: false,
              }),
            );
          }}
        >
          <StreamControls />
        </div>
      )}
    </div>
  );
};

export default HomeLiveStreamPlayerDesktopControls;
